// Saving output to disk. In Electron we use a native Save dialog + write;
// in the browser we fall back to a download. Tools call these; they never
// touch Electron APIs directly.

/** Trigger a browser download of raw data. */
const download = (name: string, data: BlobPart, mime: string) => {
  const url = URL.createObjectURL(new Blob([data], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * Save PDF bytes. Electron: native Save-As dialog then write (returns the path
 * written, or null if cancelled). Web: downloads and returns null.
 */
export const savePdf = async (
  suggestedName: string,
  bytes: Uint8Array,
): Promise<string | null> => {
  if (window.electronAPI?.saveFileDialog) {
    const target = await window.electronAPI.saveFileDialog(suggestedName);
    if (!target) return null;
    await window.electronAPI.writeFile(target, bytes);
    return target;
  }
  download(suggestedName, bytes as BlobPart, "application/pdf");
  return null;
};

/** Save multiple outputs (split parts, exported images). Electron: pick a
 *  folder once and write all into it; Web: download each. */
export const saveMany = async (
  files: Array<{ name: string; data: Uint8Array | Blob; mime: string }>,
): Promise<string | null> => {
  if (window.electronAPI?.pickDirectory && window.electronAPI?.writeFile) {
    const dir = await window.electronAPI.pickDirectory();
    if (!dir) return null;
    for (const f of files) {
      const bytes =
        f.data instanceof Blob ? new Uint8Array(await f.data.arrayBuffer()) : f.data;
      await window.electronAPI.writeFile(`${dir}/${f.name}`, bytes);
    }
    return dir;
  }
  for (const f of files) {
    const part: BlobPart = f.data instanceof Blob ? f.data : (f.data as BlobPart);
    download(f.name, part, f.mime);
  }
  return null;
};

/** Let the user pick one or more additional PDFs (for Merge). */
export const pickPdfs = async (): Promise<Array<{ name: string; bytes: Uint8Array }>> => {
  if (window.electronAPI?.pickPdfs) {
    const res = await window.electronAPI.pickPdfs();
    return res.map((r) => ({ name: r.name, bytes: r.data }));
  }
  // Web fallback: hidden multi-file input.
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf,.pdf";
    input.multiple = true;
    input.onchange = async () => {
      const files = Array.from(input.files ?? []);
      const out = await Promise.all(
        files.map(async (f) => ({ name: f.name, bytes: new Uint8Array(await f.arrayBuffer()) })),
      );
      resolve(out);
    };
    input.click();
  });
};
